import React from "react";
import { defineField, PortableTextBlock } from "sanity";

import PageCourseGalleryJSX from "@/components/pages/PageCourseGalleryJSX";
import type { IFaqItem, IFooterColumn, IFooterLink } from "@/sanity/pages/PageHomeTemplate";
import { GROQButton, GROQButtonSchema, GROQImage, GROQImageSchema } from "@/sanity/lib/definitions";
import { PageTemplate, PageTemplateSchema } from "@/sanity/templates";

import { BasePageQuery, definePageType } from "../globals/PageGlobals";

// ─────────────────────────────────────────────────────────────────────────────
// Page Course Gallery — Sanity schema
// Figma: WCE | New UI → "Course Page" → Gallery / Course (3780:19178), eight
// stacked sections at 1920 wide:
//   1. Hero            — kicker, serif headline, CTA + fanned course covers
//   2. Stats           — four "big number" cards
//   3. Master Class    — WHITE section: sticky copy column + 6 benefit cards
//   4. Mentors         — 3-column portrait grid
//   5. Disciplines     — avatar picker driving a centred card carousel
//   6. All Classes     — 16:9 course cover grid + filter
//   7. FAQ             — reuses the Home accordion section
//   8. Footer          — reuses the Home footer section
// ─────────────────────────────────────────────────────────────────────────────

/** Difficulty badge on every class card. */
export const CLASS_LEVELS = ["beginner", "intermediate", "advanced"] as const;
export type ClassLevel = (typeof CLASS_LEVELS)[number];

/** Canonical category set — also drives the order of the All Classes filter. */
export const CLASS_CATEGORIES = [
  "Fundamentals",
  "Psychology & Strategy",
  "Order Flow",
  "Macro & Investing",
  "Prop Trading",
  "Institutional",
] as const;
export type ClassCategory = (typeof CLASS_CATEGORIES)[number];

/** Material Symbols shipped as files in /public/courseGallery. */
const MASTER_CLASS_ICONS = [
  { title: "Trophy (verified track record)", value: "trophy" },
  { title: "Analytics (market analysis)", value: "analytics" },
  { title: "Group (community)", value: "group" },
  { title: "School (courses)", value: "school" },
  { title: "Laptop (watch anywhere)", value: "laptop" },
  { title: "Verified (verified strategies)", value: "verified" },
];

export const PageCourseGalleryType = definePageType({
  name: "pageCourseGallery",
  title: "Page Course Gallery",
  fieldsets: [
    { name: "hero", title: "1. Hero", options: { collapsible: true, collapsed: true } },
    { name: "stats", title: "2. Stats (big numbers)", options: { collapsible: true, collapsed: true } },
    { name: "masterClass", title: "3. Master Class (white section)", options: { collapsible: true, collapsed: true } },
    { name: "mentors", title: "4. Mentors grid", options: { collapsible: true, collapsed: true } },
    { name: "disciplines", title: "5. Disciplines carousel", options: { collapsible: true, collapsed: true } },
    { name: "classes", title: "6. All Classes", options: { collapsible: true, collapsed: true } },
    { name: "faq", title: "7. FAQ", options: { collapsible: true, collapsed: true } },
    { name: "footer", title: "8. Footer", options: { collapsible: true, collapsed: true } },
  ],
  fields: [
    // ── 1. HERO ──────────────────────────────────────────────────────────
    defineField({ name: "heroChip", title: "Chip", type: "string", fieldset: "hero" }),
    defineField({
      name: "heroTitle",
      title: "Title (richtext — italics render as the serif italic)",
      type: "richtext",
      fieldset: "hero",
    }),
    defineField({ name: "heroSubtitle", title: "Subtitle (richtext)", type: "richtext", fieldset: "hero" }),
    defineField({ name: "heroCTA", title: "CTA", type: "button", fieldset: "hero" }),
    defineField({
      name: "heroCards",
      title: "Fanned course covers (portrait 10:15, centre card is the middle entry)",
      type: "array",
      fieldset: "hero",
      of: [
        {
          type: "object",
          fields: [
            defineField({ name: "image", title: "Cover (portrait)", type: "image" }),
            defineField({ name: "name", title: "Course name [alt text]", type: "string" }),
          ],
        },
      ],
    }),

    // ── 2. STATS ─────────────────────────────────────────────────────────
    defineField({ name: "statsChip", title: "Chip", type: "string", fieldset: "stats" }),
    defineField({ name: "statsTitle", title: "Title (richtext)", type: "richtext", fieldset: "stats" }),
    defineField({
      name: "statsItems",
      title: "Stat cards",
      type: "array",
      fieldset: "stats",
      of: [
        {
          type: "object",
          fields: [
            defineField({ name: "value", title: "Big number (e.g. 9, $0, 85+)", type: "string" }),
            defineField({ name: "label", title: "Label", type: "string" }),
            defineField({ name: "description", title: "Description", type: "text" }),
          ],
          preview: { select: { title: "value", subtitle: "label" } },
        },
      ],
    }),

    // ── 3. MASTER CLASS (white) ──────────────────────────────────────────
    defineField({ name: "masterClassChip", title: "Chip", type: "string", fieldset: "masterClass" }),
    defineField({ name: "masterClassTitle", title: "Title (richtext)", type: "richtext", fieldset: "masterClass" }),
    defineField({ name: "masterClassBody", title: "Body copy", type: "text", fieldset: "masterClass" }),
    defineField({ name: "masterClassCTA", title: "CTA", type: "button", fieldset: "masterClass" }),
    defineField({
      name: "masterClassCards",
      title: "Benefit cards (the pipe meter fills in list order)",
      type: "array",
      fieldset: "masterClass",
      of: [
        {
          type: "object",
          fields: [
            defineField({
              name: "icon",
              title: "Icon",
              type: "string",
              options: { list: MASTER_CLASS_ICONS },
            }),
            defineField({ name: "text", title: "Text", type: "text" }),
          ],
          preview: { select: { title: "text", subtitle: "icon" } },
        },
      ],
    }),

    // ── 4. MENTORS ───────────────────────────────────────────────────────
    defineField({ name: "mentorsChip", title: "Chip", type: "string", fieldset: "mentors" }),
    defineField({ name: "mentorsTitle", title: "Title (richtext)", type: "richtext", fieldset: "mentors" }),
    defineField({
      name: "mentors",
      title: "Mentors (in display order)",
      type: "array",
      fieldset: "mentors",
      of: [
        {
          type: "object",
          fields: [
            defineField({ name: "image", title: "Portrait", type: "image" }),
            defineField({ name: "name", title: "Name", type: "string" }),
            defineField({ name: "title", title: "Title", type: "string" }),
          ],
          preview: { select: { title: "name", subtitle: "title", media: "image" } },
        },
      ],
    }),

    // ── 5. DISCIPLINES ───────────────────────────────────────────────────
    defineField({ name: "disciplinesChip", title: "Chip", type: "string", fieldset: "disciplines" }),
    defineField({ name: "disciplinesTitle", title: "Title (richtext)", type: "richtext", fieldset: "disciplines" }),
    defineField({ name: "disciplinesSubtitle", title: "Subtitle", type: "text", fieldset: "disciplines" }),
    defineField({
      name: "disciplines",
      title: "Disciplines (one avatar in the picker per entry)",
      type: "array",
      fieldset: "disciplines",
      of: [
        {
          type: "object",
          fields: [
            defineField({ name: "cover", title: "Cover image", type: "image" }),
            defineField({ name: "avatar", title: "Picker avatar (mentor headshot)", type: "image" }),
            defineField({ name: "category", title: "Category (e.g. Execution)", type: "string" }),
            defineField({ name: "classesLabel", title: "Classes label (e.g. 12 classes)", type: "string" }),
            defineField({ name: "durationLabel", title: "Duration label (e.g. 1h 54m)", type: "string" }),
            defineField({ name: "headline", title: "Headline", type: "text" }),
            defineField({
              name: "bullets",
              title: "What you'll learn",
              type: "array",
              of: [{ type: "string" }],
            }),
            defineField({ name: "mentorName", title: "Mentor name", type: "string" }),
            defineField({ name: "mentorRole", title: "Mentor role", type: "string" }),
          ],
          preview: { select: { title: "headline", subtitle: "category", media: "cover" } },
        },
      ],
    }),

    // ── 6. ALL CLASSES ───────────────────────────────────────────────────
    defineField({ name: "classesTitle", title: "Heading (e.g. All Classes)", type: "string", fieldset: "classes" }),
    defineField({
      name: "classesFilterLabel",
      title: "Filter control label",
      type: "string",
      fieldset: "classes",
      description:
        "The filter becomes an interactive dropdown once two or more classes below carry a Category; otherwise it renders as the static control in the design.",
    }),
    defineField({
      name: "classes",
      title: "Classes (16:9 covers)",
      type: "array",
      fieldset: "classes",
      of: [
        {
          type: "object",
          fields: [
            defineField({ name: "image", title: "Cover (16:9)", type: "image" }),
            defineField({ name: "name", title: "Title", type: "string" }),
            defineField({ name: "mentor", title: "Mentor", type: "string" }),
            defineField({
              name: "level",
              title: "Level",
              type: "string",
              options: {
                list: CLASS_LEVELS.map((value) => ({
                  title: value.charAt(0).toUpperCase() + value.slice(1),
                  value,
                })),
                layout: "radio",
                direction: "horizontal",
              },
            }),
            defineField({
              name: "category",
              title: "Category (drives the filter)",
              type: "string",
              options: { list: CLASS_CATEGORIES.map((value) => ({ title: value, value })) },
            }),
          ],
          preview: { select: { title: "name", subtitle: "category", media: "image" } },
        },
      ],
    }),

    // ── 7. FAQ ───────────────────────────────────────────────────────────
    defineField({ name: "faqChip", title: "Chip", type: "string", fieldset: "faq" }),
    defineField({ name: "faqTitle", title: "Title (richtext)", type: "richtext", fieldset: "faq" }),
    defineField({
      name: "faqItems",
      title: "Questions",
      type: "array",
      fieldset: "faq",
      of: [
        {
          type: "object",
          fields: [
            defineField({ name: "question", title: "Question", type: "string" }),
            defineField({ name: "answer", title: "Answer", type: "text" }),
          ],
          preview: { select: { title: "question" } },
        },
      ],
    }),

    // ── 8. FOOTER ────────────────────────────────────────────────────────
    defineField({ name: "footerLogo", title: "Logo", type: "image", fieldset: "footer" }),
    defineField({ name: "footerHeadline", title: "Headline", type: "string", fieldset: "footer" }),
    defineField({ name: "footerSubtext", title: "Subtext", type: "string", fieldset: "footer" }),
    defineField({ name: "footerCTA", title: "CTA", type: "button", fieldset: "footer" }),
    defineField({
      name: "footerColumns",
      title: "Link columns",
      type: "array",
      fieldset: "footer",
      of: [
        {
          type: "object",
          fields: [
            defineField({ name: "heading", title: "Heading", type: "string" }),
            defineField({
              name: "links",
              title: "Links",
              type: "array",
              of: [
                {
                  type: "object",
                  fields: [
                    defineField({ name: "label", title: "Label", type: "string" }),
                    defineField({ name: "url", title: "URL", type: "string" }),
                  ],
                },
              ],
            }),
          ],
        },
      ],
    }),
    defineField({ name: "footerCopyright", title: "Copyright", type: "string", fieldset: "footer" }),
    defineField({
      name: "footerLegalLinks",
      title: "Legal links",
      type: "array",
      fieldset: "footer",
      of: [
        {
          type: "object",
          fields: [
            defineField({ name: "label", title: "Label", type: "string" }),
            defineField({ name: "url", title: "URL", type: "string" }),
          ],
        },
      ],
    }),
  ],
});

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface ICourseCover {
  image?: GROQImage;
  name?: string;
}

export interface IStatItem {
  value?: string;
  label?: string;
  description?: string;
}

export type MasterClassIcon = "trophy" | "analytics" | "group" | "school" | "laptop" | "verified";

export interface IMasterClassCard {
  icon?: MasterClassIcon;
  text?: string;
}

export interface IGalleryMentor {
  image?: GROQImage;
  name?: string;
  title?: string;
}

export interface IDiscipline {
  cover?: GROQImage;
  avatar?: GROQImage;
  category?: string;
  classesLabel?: string;
  durationLabel?: string;
  headline?: string;
  bullets?: string[];
  mentorName?: string;
  mentorRole?: string;
}

export interface IClassCard {
  image?: GROQImage;
  name?: string;
  mentor?: string;
  level?: ClassLevel;
  category?: ClassCategory;
}

export interface PageCourseGallerySchema extends PageTemplateSchema {
  // Hero
  heroChip?: string;
  heroTitle?: PortableTextBlock[];
  heroSubtitle?: PortableTextBlock[];
  heroCTA?: GROQButton;
  heroCards?: ICourseCover[];

  // Stats
  statsChip?: string;
  statsTitle?: PortableTextBlock[];
  statsItems?: IStatItem[];

  // Master Class
  masterClassChip?: string;
  masterClassTitle?: PortableTextBlock[];
  masterClassBody?: string;
  masterClassCTA?: GROQButton;
  masterClassCards?: IMasterClassCard[];

  // Mentors
  mentorsChip?: string;
  mentorsTitle?: PortableTextBlock[];
  mentors?: IGalleryMentor[];

  // Disciplines
  disciplinesChip?: string;
  disciplinesTitle?: PortableTextBlock[];
  disciplinesSubtitle?: string;
  disciplines?: IDiscipline[];

  // All Classes
  classesTitle?: string;
  classesFilterLabel?: string;
  classes?: IClassCard[];

  // FAQ
  faqChip?: string;
  faqTitle?: PortableTextBlock[];
  faqItems?: IFaqItem[];

  // Footer
  footerLogo?: GROQImage;
  footerHeadline?: string;
  footerSubtext?: string;
  footerCTA?: GROQButton;
  footerColumns?: IFooterColumn[];
  footerCopyright?: string;
  footerLegalLinks?: IFooterLink[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Template + GROQ query
// ─────────────────────────────────────────────────────────────────────────────

export class PageCourseGalleryTemplate implements PageTemplate<PageCourseGallerySchema> {
  title: string = "Course Gallery";
  documentType: string = "pageCourseGallery";
  schema: PageCourseGallerySchema | undefined;

  query: (id: string) => string = (id: string) => `
    *[_type == "pageCourseGallery" && _id == "${id}"][0]{
      _type,
      _id,
      _rev,
      ${BasePageQuery},

      // Hero
      heroChip,
      heroTitle,
      heroSubtitle,
      heroCTA${GROQButtonSchema},
      heroCards[]{
        "image": image${GROQImageSchema},
        name
      },

      // Stats
      statsChip,
      statsTitle,
      statsItems[]{
        value,
        label,
        description
      },

      // Master Class
      masterClassChip,
      masterClassTitle,
      masterClassBody,
      masterClassCTA${GROQButtonSchema},
      masterClassCards[]{
        icon,
        text
      },

      // Mentors
      mentorsChip,
      mentorsTitle,
      mentors[]{
        "image": image${GROQImageSchema},
        name,
        title
      },

      // Disciplines
      disciplinesChip,
      disciplinesTitle,
      disciplinesSubtitle,
      disciplines[]{
        "cover": cover${GROQImageSchema},
        "avatar": avatar${GROQImageSchema},
        category,
        classesLabel,
        durationLabel,
        headline,
        bullets,
        mentorName,
        mentorRole
      },

      // All Classes
      classesTitle,
      classesFilterLabel,
      classes[]{
        "image": image${GROQImageSchema},
        name,
        mentor,
        level,
        category
      },

      // FAQ
      faqChip,
      faqTitle,
      faqItems[]{
        question,
        answer
      },

      // Footer
      "footerLogo": footerLogo${GROQImageSchema},
      footerHeadline,
      footerSubtext,
      footerCTA${GROQButtonSchema},
      footerColumns[]{
        heading,
        links[]{
          label,
          url
        }
      },
      footerCopyright,
      footerLegalLinks[]{
        label,
        url
      },
    }
  `;

  component(data: PageCourseGallerySchema): JSX.Element {
    return <PageCourseGalleryJSX data={data} />;
  }
}

export default PageCourseGalleryTemplate;
